import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  ShoppingBag,
  Sparkles,
  ChevronRight,
  Phone,
  MapPin,
  UtensilsCrossed,
} from "lucide-react";
import { useCart } from "../App";
import { businessInfo } from "../data/business";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Menu", path: "/menu" },
  { name: "Gallery", path: "/gallery" },
  { name: "Social", path: "/social" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartCount, tableId } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-stone-200/70 py-2.5"
            : "bg-[#FFFDF9]/95 backdrop-blur-sm border-b border-stone-200/50 py-3.5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* LOGO & BRAND */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src="/logo.png"
              alt="HimCream Naturals"
              className="h-9 sm:h-10 w-auto object-contain transition-transform group-hover:scale-105"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <div className="flex flex-col">
              <span className="font-extrabold text-lg sm:text-xl text-[#2C3E50] tracking-tight leading-none">
                HimCream <span className="text-sky-600">Naturals</span>
              </span>
              <span className="text-[10px] text-stone-500 font-medium tracking-wide uppercase mt-0.5">
                Udaipur • 100% Real Fruit
              </span>
            </div>
          </Link>

          {/* DESKTOP NAV LINKS */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                  isActive(link.path)
                    ? "bg-sky-50 text-sky-700 font-semibold shadow-xs"
                    : "text-stone-600 hover:text-sky-600 hover:bg-stone-100/70"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* RIGHT ACTIONS: CART & ORDER NOW CTA */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Cart shortcut (if items exist) */}
            {cartCount > 0 && (
              <button
                onClick={() => navigate("/cart")}
                className="relative p-2 text-stone-700 hover:text-sky-600 bg-stone-100 hover:bg-sky-50 rounded-full transition"
                title="View Cart"
              >
                <ShoppingBag className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 bg-sky-600 text-white text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow">
                  {cartCount}
                </span>
              </button>
            )}

            {/* ORDER NOW / SELECT TABLE CTA */}
            <Link
              to="/select-table"
              className="hidden sm:inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white text-xs sm:text-sm font-bold px-4 sm:px-5 py-2 sm:py-2.5 rounded-full shadow-md shadow-sky-500/20 hover:shadow-lg hover:scale-105 active:scale-95 transition-all whitespace-nowrap"
            >
              <UtensilsCrossed className="w-3.5 h-3.5" />
              <span>Select Table / Order Now</span>
            </Link>

            {/* MOBILE HAMBURGER BUTTON */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 rounded-xl text-stone-700 hover:bg-stone-100 transition"
              aria-label="Toggle navigation menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER OVERLAY */}
      {isOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-stone-900/40 backdrop-blur-xs transition-opacity"
            onClick={() => setIsOpen(false)}
          />

          {/* Drawer content */}
          <div className="fixed inset-y-0 right-0 max-w-xs w-full bg-[#FFFDF9] shadow-2xl p-6 flex flex-col justify-between overflow-y-auto border-l border-stone-200">
            <div>
              {/* Header inside drawer */}
              <div className="flex items-center justify-between pb-4 border-b border-stone-200">
                <div className="flex items-center gap-2">
                  <img src="/logo.png" alt="HimCream" className="h-8 w-auto" />
                  <span className="font-extrabold text-stone-800">HimCream</span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 text-stone-500 hover:bg-stone-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Navigation Links */}
              <nav className="mt-6 flex flex-col space-y-1.5">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition ${
                      isActive(link.path)
                        ? "bg-sky-500 text-white font-semibold shadow-xs"
                        : "text-stone-700 hover:bg-stone-100"
                    }`}
                  >
                    <span>{link.name}</span>
                    <ChevronRight className="w-4 h-4 opacity-70" />
                  </Link>
                ))}
              </nav>

              {/* Order at table card */}
              <div className="mt-6 p-4 rounded-2xl bg-gradient-to-br from-sky-50 to-amber-50 border border-sky-100">
                <div className="flex items-center gap-2 text-sky-800 font-bold text-sm mb-1">
                  <Sparkles className="w-4 h-4 text-sky-600" />
                  <span>Dine-in at Café?</span>
                </div>
                <p className="text-xs text-stone-600 mb-3">
                  Scan QR on your table or select table number to order directly.
                </p>
                <Link
                  to="/select-table"
                  className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold py-2.5 rounded-xl shadow transition"
                >
                  <UtensilsCrossed className="w-4 h-4" />
                  <span>Select Table / Order Now</span>
                </Link>
              </div>
            </div>

            {/* Drawer footer info */}
            <div className="pt-6 border-t border-stone-200 text-xs text-stone-500 space-y-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                <span>Sec 4, Mali Colony Road, Udaipur</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                <a href={`tel:${businessInfo.contact.phone}`} className="hover:text-sky-600">
                  {businessInfo.contact.phoneDisplay}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
